import axios from 'axios';
import { isAuthenticated, getAccessToken } from '../features/auth/services/auth';
import { ContactRoles } from '../types/types';
import { apiSlice } from '../services/apiSlice';
import { getRoles } from '@testing-library/dom';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const getAuthHeaders = async () => {
    const isAuth = await isAuthenticated();
    if (!isAuth) throw new Error('User is not authenticated');
    const token = getAccessToken();
    return {
        Authorization: `Bearer ${token}`,
        'ngrok-skip-browser-warning': '69420',
    };
};

function buildQueryParams(params: Record<string, any>): string {
    const query = new URLSearchParams();
    for (const key in params) {
        const value = params[key];

        if (
            value === undefined ||
            value === null ||
            (typeof value === 'string' && value.trim() === '') ||
            (Array.isArray(value) && value.length === 0)
        ) {
            continue; // Skip empty values
        }

        if (Array.isArray(value)) {
            query.append(key, value.join(','));
        } else {
            query.append(key, value.toString());
        }
    }

    return query.toString();
}

export const contactRolesApi: ContactRoles = {

    createRole: async (roleData: any) => {
        const headers = await getAuthHeaders();
        const response = await axios.post(`${API_BASE_URL}/api/contact-roles`, roleData, { headers });
        return response.data;
    },

    getRoleById: async (id: number) => {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${API_BASE_URL}/api/contact-roles/${id}`, { headers });
        return response.data;
    },

    updateRole: async (id: number, roleData: any) => {
        const headers = await getAuthHeaders();
        const response = await axios.put(`${API_BASE_URL}/api/contact-roles/${id}`, roleData, { headers });
        return response.data;
    },

    getAllPermissions: async () => {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${API_BASE_URL}/api/contact-roles/all-permissions`, { headers })
        return response.data;

    },

    revokePermissions: async (id: string) => {
        const headers = await getAuthHeaders();
        try {
            const response = await axios.delete(`${API_BASE_URL}/api/contact-roles/${id}/permissions`, { headers });
            return response.data;
        } catch (error) {
            throw new Error('Failed to delete role');
        }
    },

    getContactRoles: async (
        page: number,
        perPage: number,
        sort?: { field: string; direction: 'ASC' | 'DESC' },
        query?: string,
        filters: Record<string, any> = {},
        visibleColumns: string[] = []
    ) => {
        const headers = await getAuthHeaders();
        const params: Record<string, any> = {
            page,
            perPage,
            query,
            ...filters,
            listingColumns: visibleColumns.length > 0 ? visibleColumns : undefined,
        };

        if (sort) {
            // if (sort.field === 'name') {
            //     params.sort = 'firstName';
            //     params.sortDir = sort.direction;

            // } else {
            params.sort = sort.field;
            params.sortDir = sort.direction;
            // }
        }
        const queryString = buildQueryParams(params);
        const response = await axios.get(`${API_BASE_URL}/api/contact-roles?${queryString}`, { headers });
        return response.data;
    },

}

export const contactRolesApiSlice = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        revokePermissions: builder.mutation({
            query: (id) => ({
                url: `/contact-roles/${id}/permissions`,
                method: "DELETE",
            }),
        }),

        removeRole: builder.mutation({
            query: (id) => ({
                url: `/contact-roles/assignments/${id}`,
                method: 'DELETE',
            }),
        }),

        deleteRole: builder.mutation({
            query: (id) => ({
                url: `/contact-roles/${id}`,
                method: 'DELETE',
            }),
        }),

        getRolesById: builder.query({
            query: (id) => ({
                url: `/contact-roles/${id}`,
                method: "GET",
            }),
        }),

        getRolesContacts: builder.query({
            query: ({ id, ...params}) => ({
                url: `/contact-roles/${id}/contacts?${buildQueryParams(params)}`,
                method: "GET",
            }),
        }),

    }),
});

export const { useRevokePermissionsMutation, useGetRolesByIdQuery, useRemoveRoleMutation, useDeleteRoleMutation, useGetRolesContactsQuery } = contactRolesApiSlice